import { get_token } from "@/router";
import {
    add_days_to_iso_date,
    ajax_json_request,
    build_table_rows,
    build_tariff_lookup,
    build_url_with_query,
    ensure_trailing_slash,
    extract_rate_records,
    extract_tariff_records,
    format_settings_date,
    get_assignment_property_id,
    get_assignment_tariff_id,
    get_positive_integer_id,
    merge_assignment_records,
    normalize_assignment_effective_from,
    normalize_list_rate_record,
    normalize_properties_response,
    normalize_tariff_record
} from "@/components/Settings/settings_utils";

const properties_url = ensure_trailing_slash(process.env.VUE_APP_PROPERTIES_URL);
const client_app = process.env.VUE_APP_CLIENT_APP || "bcx";
const municipalities_url = ensure_trailing_slash(
    process.env.VUE_APP_MUNICIPALITIES_URL || properties_url.replace(/properties\/?$/i, "municipalities/")
);
const tariff_books_url = ensure_trailing_slash(process.env.VUE_APP_TARIFF_BOOKS_URL);
const tariffs_url = ensure_trailing_slash(
    process.env.VUE_APP_TARIFFS_URL || tariff_books_url.replace(/tariff-books\/?$/i, "tariffs/")
);
const property_tariff_assignments_url = ensure_trailing_slash(process.env.VUE_APP_PROPERTY_TARIFF_ASSIGNMENTS_URL);
const tariff_rates_url = ensure_trailing_slash(
    process.env.VUE_APP_RATES_URL
    || process.env.VUE_APP_TARIFF_RATES_URL?.replace(/tariff-rates\/?$/i, "rates/")
    || tariff_books_url.replace(/tariff-books\/?$/i, "rates/")
);

export default {
    get_auth_headers(include_json_content_type = true) {
        const token = get_token();
        const headers = {
            Accept: "application/json"
        };

        if (include_json_content_type) {
            headers["Content-Type"] = "application/json";
        }

        if (token) {
            headers.Authorization = `Token ${token}`;
        }

        return headers;
    },

    async fetch_settings_json(request_url, options = {}) {
        if (!request_url) {
            throw new Error("Settings endpoint is not configured.");
        }

        return ajax_json_request(request_url, options);
    },

    get_assignment_base_url() {
        return property_tariff_assignments_url;
    },

    get_assignment_create_url() {
        return `${this.get_assignment_base_url()}create/`;
    },

    get_assignment_toggle_url() {
        return `${this.get_assignment_base_url()}toggle/`;
    },

    get_assignment_update_url(assignment_id) {
        const safe_assignment_id = get_positive_integer_id(assignment_id);

        if (!safe_assignment_id) {
            return "";
        }

        return `${this.get_assignment_base_url()}${safe_assignment_id}/update/`;
    },

    get_tariff_update_url(tariff_id) {
        const safe_tariff_id = get_positive_integer_id(tariff_id);

        if (!safe_tariff_id || !tariffs_url) {
            return "";
        }

        return `${tariffs_url}${safe_tariff_id}/update/`;
    },

    async fetch_assignment_json(request_url, options = {}) {
        if (!request_url) {
            throw new Error("A valid tariff assignment is required.");
        }

        return ajax_json_request(request_url, options);
    },

    format_date(date_string) {
        return format_settings_date(date_string);
    },

    format_property_effective_date(date_string) {
        if (!date_string) {
            return "Open-ended";
        }

        const normalized_date = String(date_string).slice(0, 10);

        return normalized_date.includes("-")
            ? normalized_date.replaceAll("-", "/")
            : normalized_date;
    },

    get_assignment_date_range(row) {
        const effective_from = row.rate_effective_from || row.effective_from;
        const effective_to = row.rate_effective_to || row.effective_to;

        return `${this.format_property_effective_date(effective_from)} - ${this.format_property_effective_date(effective_to)}`;
    },

    get_next_available_assignment_effective_from(property_id, requested_effective_from) {
        const normalized_property_id = Number(property_id);
        let next_effective_from = String(requested_effective_from || "");

        if (!Number.isInteger(normalized_property_id) || normalized_property_id <= 0 || !next_effective_from) {
            return next_effective_from;
        }

        const latest_existing_effective_from = this.property_tariff_assignments
            .filter((assignment) => get_assignment_property_id(assignment) === normalized_property_id)
            .map((assignment) => normalize_assignment_effective_from(assignment))
            .filter(Boolean)
            .sort()
            .at(-1);

        return latest_existing_effective_from && next_effective_from <= latest_existing_effective_from
            ? add_days_to_iso_date(latest_existing_effective_from, 1)
            : next_effective_from;
    },

    get_assignment_toggle_payload(assignment) {
        if (assignment?.id) {
            return ({
                assignment_id: Number(assignment.id),
                is_active: true
            });
        }

        return ({
            property_id: Number(this.new_tariff.property_id),
            tariff_id: Number(this.new_tariff.tariff_id),
            is_active: true
        });
    },

    should_create_after_toggle_error(error) {
        const message = String(error?.message || "").toLowerCase();

        return [
            "not found",
            "not_found",
            "does not exist",
            "no assignment",
            "invalid_assignment",
            "assignment_id"
        ].some((error_fragment) => message.includes(error_fragment));
    },

    async reactivate_assignment(assignment) {
        await this.fetch_assignment_json(
            this.get_assignment_toggle_url(),
            {
                method: "POST",
                headers: this.get_auth_headers(true),
                body: JSON.stringify(this.get_assignment_toggle_payload(assignment))
            }
        );
    },

    async create_assignment(payload, request_url) {
        await this.fetch_assignment_json(
            request_url,
            {
                method: "POST",
                headers: this.get_auth_headers(true),
                body: JSON.stringify(payload)
            }
        ).catch(async (error) => {
            const message = String(error?.message || "").toLowerCase();

            if (
                (message.includes("property_id") && message.includes("effective_from")) ||
                message.includes("unique")
            ) {
                const next_effective_from = add_days_to_iso_date(
                    String(payload?.effective_from || new Date().toISOString().slice(0, 10)),
                    1
                );

                const retry_payload = {
                    ...payload,
                    effective_from: next_effective_from
                };

                if (this?.new_tariff && typeof this.new_tariff === "object") {
                    this.new_tariff = {
                        ...this.new_tariff,
                        effective_from: next_effective_from
                    };
                }

                return this.fetch_assignment_json(
                    request_url,
                    {
                        method: "POST",
                        headers: this.get_auth_headers(true),
                        body: JSON.stringify(retry_payload)
                    }
                );
            }

            throw error;
        });
    },

    async save_assignment(payload, request_url, reusable_assignment) {
        if (reusable_assignment) {
            await this.reactivate_assignment(reusable_assignment);
            return;
        }

        await this.create_assignment(payload, request_url);
    },

    find_inactive_assignment(property_id, tariff_id) {
        const normalized_property_id = Number(property_id);
        const normalized_tariff_id = Number(tariff_id);

        if (!Number.isInteger(normalized_property_id) || !Number.isInteger(normalized_tariff_id)) {
            return null;
        }

        return this.property_tariff_assignments.find((assignment) => {
            return !assignment?.is_active
                && get_assignment_property_id(assignment) === normalized_property_id
                && get_assignment_tariff_id(assignment) === normalized_tariff_id;
        }) || null;
    },

    async load_tariff_settings_data() {
        this.is_loading = true;
        this.clear_load_error?.();
        this.load_error = "";

        try {
            const requests = [
                this.fetch_settings_json(build_url_with_query(properties_url, { client_app }), {
                    method: "GET",
                    headers: this.get_auth_headers(false)
                }),
                this.fetch_settings_json(`${municipalities_url}?active_only=true`, {
                    method: "GET",
                    headers: this.get_auth_headers(false)
                }).catch(() => []),
                this.fetch_settings_json(tariff_books_url, {
                    method: "GET",
                    headers: this.get_auth_headers(false)
                }),
                this.fetch_settings_json(tariffs_url, {
                    method: "GET",
                    headers: this.get_auth_headers(false)
                }),
                this.fetch_settings_json(property_tariff_assignments_url, {
                    method: "GET",
                    headers: this.get_auth_headers(false)
                }),
                this.fetch_settings_json(build_url_with_query(property_tariff_assignments_url, {
                    active_only: "false",
                    include_inactive: "true"
                }), {
                    method: "GET",
                    headers: this.get_auth_headers(false)
                }).catch(() => [])
            ];

            if (tariff_rates_url) {
                requests.push(
                    this.fetch_settings_json(tariff_rates_url, {
                        method: "GET",
                        headers: this.get_auth_headers(false)
                    }).catch(() => [])
                );
            }

            const [
                properties,
                municipalities,
                tariff_books,
                tariffs,
                assignments,
                assignment_history,
                tariff_rates = []
            ] = await Promise.all(requests);
            const normalized_tariffs = extract_tariff_records(tariffs).map((tariff) => normalize_tariff_record(tariff));
            const normalized_tariff_rates = extract_rate_records(tariff_rates).map((rate) => normalize_list_rate_record(rate));
            const tariff_lookup = build_tariff_lookup(extract_tariff_records(tariffs));

            this.properties = normalize_properties_response(properties);
            this.municipalities = Array.isArray(municipalities) ? municipalities : [];
            this.tariff_books = Array.isArray(tariff_books) ? tariff_books : [];
            this.tariffs = normalized_tariffs;
            this.tariff_rates = normalized_tariff_rates;
            this.property_tariff_assignments = merge_assignment_records(assignments, assignment_history);
            this.tariff_rows = build_table_rows(
                this.properties,
                this.property_tariff_assignments,
                tariff_lookup,
                normalized_tariff_rates
            );
        } catch (error) {
            const message = error.message || "Unable to load tariff settings data.";
            if (this.set_load_error) {
                this.set_load_error(message);
            } else {
                this.load_error = message;
            }
        } finally {
            this.is_loading = false;
        }
    }
};
